import React from "react";
import ReactDOM from "react-dom";
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import "./styles/global.css";

// Material Dashboard 2 PRO React TS Context Provider
import { MaterialUIControllerProvider } from "context";

import { createApolloClient } from "services/apolloclient";

import * as Sentry from "@sentry/react";
import useAbortLikeError from "hooks/utils/useAbortLikeError";
import useAbortLikeUnhandledRejection from "hooks/utils/useAbortLikeUnhandledRejection";
import { getUnexpectedGraphQLErrors, parseGraphQLErrors } from "utils/sentry/graphqlErrors";
import ErrorFallback from "components/ErrorPage";

const preventNumberInputScrollChange = (event: WheelEvent) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }

  if (target.type !== "number") {
    return;
  }

  if (document.activeElement === target) {
    event.preventDefault();
  }
};

window.addEventListener("wheel", preventNumberInputScrollChange, { passive: false });

const apolloClient = createApolloClient();
let hasInitializedSentry = false;

function Root() {
  const isAbortLikeError = useAbortLikeError();

  useAbortLikeUnhandledRejection();

  React.useEffect(() => {
    if (hasInitializedSentry) {
      return;
    }

    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [new Sentry.BrowserTracing(), new Sentry.Replay()],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      tracesSampleRate: 0.25,
      beforeSend(event, hint) {
        const error = hint.originalException || hint.syntheticException;
        if (isAbortLikeError(error)) {
          return null;
        }

        // Suppress GraphQL events that only contain expected auth or user-correctable validation noise.
        // @ts-ignore
        const errorGraphQLErrors = parseGraphQLErrors(error?.graphQLErrors);
        if (errorGraphQLErrors.length && getUnexpectedGraphQLErrors(errorGraphQLErrors).length === 0) {
          return null;
        }

        const eventGraphQLErrors = parseGraphQLErrors(event?.extra?.graphqlErrors);
        const eventOperationName =
          typeof event?.extra?.operationName === "string" ? event.extra.operationName : null;
        if (
          eventGraphQLErrors.length &&
          getUnexpectedGraphQLErrors(eventGraphQLErrors, eventOperationName).length === 0
        ) {
          return null;
        }

        // @ts-ignore
        if (error && error.message) {
          // @ts-ignore
          if (error.message.indexOf("_leaflet_pos") > -1) {
            return null;
          }

          // @ts-ignore
          if (error.message.indexOf("Invalid credentials") > -1) {
            return null;
          }

          // @ts-ignore
          if (error.message.indexOf("Turnstile") > -1) {
            return null;
          }
        }

        return event;
      },
    });

    hasInitializedSentry = true;
  }, [isAbortLikeError]);

  return (
    <Sentry.ErrorBoundary fallback={({ error }) => <ErrorFallback error={error} />}>
      <BrowserRouter>
        <MaterialUIControllerProvider>
          <App apolloClient={apolloClient} />
        </MaterialUIControllerProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

ReactDOM.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
  document.getElementById("root")
);
